import {
  SketchElement,
  StackingOrder,
  isFrameElement,
  isLayerElement,
} from '../../../inspector'
import { FlatTreeNode } from './types'

/**
 * Get an element's children in the order used by Sketch layer lists.
 *
 * Unlike the document model where stack item order is preserved and the
 * stacking order only affects rendering, this tree is constructed from the
 * Marina rendering tree. Its children are already ordered by stacking order,
 * so backwards stacks need to be reversed to match the Mac layer list order.
 */
export function getChildrenInLayerListOrder(
  element: SketchElement
): SketchElement[] {
  if (
    isFrameElement(element) &&
    element.stack?.stackingOrder === StackingOrder.Backwards
  ) {
    return [...element.children].reverse()
  }

  return element.children
}

/**
 * Find all ancestor identifiers for a given element by walking up the parent chain
 */
export function findAncestorIdentifiers(element: SketchElement): string[] {
  const ancestors: string[] = []
  let current = element.parent

  while (current) {
    ancestors.push(current.elementUUID)
    current = current.parent
  }

  return ancestors
}

/**
 * Flatten the tree structure into a list removing collapsed layers
 */
export function flattenTree(
  elements: SketchElement[],
  collapsedElements: Set<string>,
  level = 0
): FlatTreeNode[] {
  const result: FlatTreeNode[] = elements.reduce<FlatTreeNode[]>(
    (acc, element) => {
      const hasChildren = element.children.length > 0
      const isCollapsed = collapsedElements.has(element.elementUUID)
      const isLayerMasked = isLayerElement(element)
        ? element.isLayerMasked
        : false

      acc.push({
        sketchElement: element,
        level,
        hasChildren,
        isCollapsed,
        isLayerMasked,
      })

      // Recursively add children if not collapsed
      if (hasChildren && !isCollapsed) {
        const children = getChildrenInLayerListOrder(element)
        acc.push(...flattenTree(children, collapsedElements, level + 1))
      }

      return acc
    },
    []
  )

  return result
}

/**
 * Filter tree based on search query - returns a new tree with only elements that match
 * or have matching descendants
 */
export function filterTree(
  elements: SketchElement[],
  searchQuery: string
): SketchElement[] {
  if (!searchQuery) return elements

  const query = searchQuery.toLowerCase()

  return elements.reduce<SketchElement[]>((acc, element) => {
    const matches = element.name.toLowerCase().includes(query)
    const filteredChildren = filterTree(element.children, searchQuery)

    // Include element if it matches or has matching children
    if (matches || filteredChildren.length > 0) {
      acc.push({
        ...element,
        children: filteredChildren,
      })
    }

    return acc
  }, [])
}
